import { Component, EventEmitter, OnChanges, Output, input, output } from '@angular/core';

import { TDashlet } from 'src/app/project/modules/preferences/preferences.model';

import { ModalService } from 'src/app/project/components/modal/modal.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventDashboard,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EIconPath } from '../../../../shared/enums/icons.enum';
import { AccountService } from '../../../account/account-service/account.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { EDashletType } from '../../dashlets-enums';
import { EditCostComparisonDashletModalComponent } from './edit-cost-comparison-dashlet-modal/edit-cost-comparison-dashlet-modal.component';
import { TEditCostComparisonModalData } from './edit-cost-comparison-dashlet-modal/edit-cost-comparison-modal.consts';
import { EditValueBreakdownDashletModalComponent } from './edit-value-breakdown-dashlet-modal/edit-value-breakdown-dashlet-modal.component';
import { getAccountHeaderAccountName } from './utils/get-dashlet-header-account-name';
import { getDashletHeaderSiteName } from './utils/get-dashlet-header-site-name';

@Component({
  selector: 'pp-dashlet-head',
  templateUrl: './dashlet-head.component.html',
  styleUrls: ['./dashlet-head.component.scss'],
  standalone: false,
})
export class DashletHeadComponent implements OnChanges {
  ppDashlet = input<TDashlet>();
  ppAssetId = input<string>();
  ppCanEdit = input<boolean>();
  ppAccountName = input<string>();
  ppWorkspaces = input<string[]>(); // hacks around Angular change detection so it reruns when you change workspace ids
  @Output() ppToggleFlip = new EventEmitter();
  ppDashletChange = output<TDashlet>();
  ppSave = output<TDashlet>();

  EIconPath = EIconPath;
  siteName: string;
  private sitesNames: string[];
  siteTooltip: string;
  accountName: string;
  private accountNames: string[] = [];
  accountTooltip: string;
  EDashletType = EDashletType;
  title: string;
  tooltipText: string;
  renameDashletsEnabled: boolean;

  renamableDashlets: EDashletType[] = [
    EDashletType.COST_COMPARISON,
    EDashletType.CURRENT_PRIORITY,
    EDashletType.CURRENT_STACKED_STATUS,
    EDashletType.CURRENT_STATUS,
    EDashletType.OVER_TIME_PRIORITY,
    EDashletType.OVER_TIME_STATUS,
    EDashletType.VALUE_BREAKDOWN,
  ];

  constructor(
    private workspaceService: WorkspaceService,
    private accountService: AccountService,
    private translationPipe: TranslationPipe,
    private modalService: ModalService,
  ) {}

  ngOnChanges(): void {
    this.renameDashletsEnabled =
      this.ppAssetId() !== undefined && this.renamableDashlets.includes(this.ppDashlet().name);

    const accountData = getAccountHeaderAccountName(
      this.ppAccountName(),
      this.ppWorkspaces(),
      this.workspaceService.getWorkspaces(),
      this.accountService.getAccounts(),
    );
    this.accountName = accountData.accountName;
    this.accountTooltip = accountData.tooltip;
    const siteData = getDashletHeaderSiteName(
      this.ppWorkspaces(),
      this.workspaceService.getWorkspaces(),
    );
    this.siteName = siteData.siteName;
    this.siteTooltip = siteData.tooltip;
    this.setTitle();
  }

  flipDashlet(): void {
    const ppDashlet = this.ppDashlet();
    if (ppDashlet.name === EDashletType.COST_COMPARISON) {
      this.modalService.setData<TEditCostComparisonModalData>({
        dashlet: ppDashlet,
        assetId: this.ppAssetId(),
      });

      this.modalService.showModal(EditCostComparisonDashletModalComponent);

      return;
    } else if (ppDashlet.name === EDashletType.VALUE_BREAKDOWN) {
      this.modalService.setData({
        dashlet: ppDashlet,
        assetId: this.ppAssetId(),
      });

      this.modalService.showModal(EditValueBreakdownDashletModalComponent);

      return;
    }

    logEventInGTAG(EGoogleEventDashboard.DASHBOARD__DASHLET__FLIP, {
      source: EGoogleEventCategory.DASHBOARD,
      type: ppDashlet.name,
    });

    this.ppToggleFlip.emit();
  }

  onDashletChange(updatedDashlet: TDashlet): void {
    this.ppDashletChange.emit(updatedDashlet);
  }

  onDashletSave(): void {
    this.ppSave.emit(this.ppDashlet());
  }

  private setTitle(): void {
    switch (this.ppDashlet().name) {
      case EDashletType.ACTIVITIES:
        this.title = this.translationPipe.transform('activity');
        this.tooltipText = '';
        break;
      case EDashletType.CURRENT_STATUS:
        this.title = this.translationPipe.transform('dashlets_live_status');
        this.tooltipText = '';
        break;
      case EDashletType.CURRENT_PRIORITY:
        this.title = this.translationPipe.transform('dashlets_live_priority');
        this.tooltipText = '';
        break;
      case EDashletType.OVER_TIME_STATUS:
        this.title = this.translationPipe.transform('dashlet_over_time_status');
        this.tooltipText = '';
        break;
      case EDashletType.OVER_TIME_PRIORITY:
        this.title = this.translationPipe.transform('dashlet_over_time_priority');
        this.tooltipText = '';
        break;
      case EDashletType.CURRENT_STACKED_STATUS:
        this.title = this.translationPipe.transform('dashlet_stacked_status');
        this.tooltipText = '';
        break;
      case EDashletType.COST_COMPARISON:
        this.title = this.translationPipe.transform('dashlet_cost_comparison');
        this.tooltipText = this.translationPipe.transform('dashlet_cost_comparison_tooltip');
        break;
      case EDashletType.VALUE_BREAKDOWN:
        this.title = this.translationPipe.transform('dashlet_value_breakdown');
        this.tooltipText = '';
        break;
      default:
        this.tooltipText = '';
        this.title = '';
    }
  }
}
